import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Icon from '../components/icon/Icon';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import { getG2Header, getG2Products } from '../services/GuaranteesService';
import { routes } from '../routes/routePaths';
import type { GuaranteesListState } from '../utils/guaranteesListState';
import { useClientTheme } from '../context/ThemeContext';
import Badge from '../components/badge/Badge';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import PageBreadcrumb from '../components/PageBreadcrumb/PageBreadcrumb';
import { useConfig } from '../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../utils/quotationAssets';
import { isoCodeForGuaranteeCurrency } from '../utils/currencyLabel';

interface G2Header {
  cuit: string;
  capArs: number;
  capUsd: number;
  usedArs: number;
  usedUsd: number;
  availableArs: number;
  availableUsd: number;
}

interface G2Product {
  cuit: string;
  productId: number;
  productLabel: string;
  currencyId: number;
  currencyLabel: string;
  capProduct: number;
  usedProduct: number;
  availableProduct: number;
  availableCuitCurrency: number;
  availableOperable: number;
}

const GuaranteesDetail = () => {
  const fiscalId = useFiscalIdConfig();
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const { cuit } = useParams<{ cuit: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientTheme } = useClientTheme();
  const [header, setHeader] = useState<G2Header | null>(null);
  const [products, setProducts] = useState<G2Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cuit) {
      fetchData();
    }
  }, [cuit]);

  const fetchData = async () => {
    if (!cuit) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [headerRes, productsRes] = await Promise.all([
        getG2Header(cuit),
        getG2Products(cuit)
      ]);
      
      if (headerRes.success && 'data' in headerRes) {
        setHeader(headerRes.data);
      }
      if (productsRes.success && 'data' in productsRes) {
        setProducts(productsRes.data);
      }
    } catch (err) {
      setError('Error al cargar los datos del aval');
    } finally {
      setLoading(false);
    }
  };

  const listState = location.state as GuaranteesListState | null;
  const companyCrumb = listState?.companyName?.trim() || cuit || '';

  const handleGoBack = () => {
    navigate(routes.guarantees, { state: listState ?? undefined });
  };

  const handleProductClick = (product: G2Product) => {
    if (!cuit) return;
    const path = routes.groupedProductLineDetail
      .replace(':cuit', cuit)
      .replace(':productLineCode', String(product.productId))
      .replace(':currencyId', String(product.currencyId));
    if (listState) {
      navigate(path, { state: listState });
    } else {
      navigate(path);
    }
  };

  const breadcrumb = (
    <PageBreadcrumb
      items={[
        { label: 'Inicio', to: routes.dashboard },
        { label: 'Gestión de avales', to: routes.guarantees, state: listState ?? undefined },
        ...(companyCrumb ? [{ label: companyCrumb }] : []),
      ]}
    />
  );

  const formatNumber = (amount: number) => {
    // Si es un número entero, no mostrar decimales
    if (amount % 1 === 0) {
      return amount.toLocaleString('es-AR');
    }
    // Si tiene decimales, usar formato argentino (coma como separador decimal)
    return amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const renderSummaryCard = (title: string, arsAmount: number, usdAmount: number) => {
    const isDisabled = arsAmount === 0 && usdAmount === 0;
    
    return (
      <Card variant='neutral' modifier='regular' spacing={'4'}>
        <H variant="secondary-2" withMargin={false} type='4' weight='bold' modifier={isDisabled ? 'weak' : 'strong'}>
          {title}
        </H>
        <Card margin={{ top: '2' }}>
          <H variant='secondary-2' type="2" weight="bold" modifier={isDisabled ? 'weak' : 'regular'} withMargin={false}>
            {localCurrencyCode} {formatNumber(arsAmount)}
          </H>
          <H variant='secondary-2' type="2" weight="bold" modifier={isDisabled ? 'weak' : 'regular'} withMargin={false}>
            USD {formatNumber(usdAmount)}
          </H>
        </Card>
      </Card>
    );
  };

  const renderProductsTable = () => {
    if (!products.length) return null;

    const isOperable = (row: G2Product) => row.availableOperable > 0;

    const columns = [
      {
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Producto</P>,
        selector: (row: G2Product) => (
          <P variant='secondary-2' type="3" weight="regular" withMargin={false} modifier='regular'>{row.productLabel}</P>
        ),
        grow: 1,
      },
      {
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Divisa</P>,
        selector: (row: G2Product) => (
          <P variant='secondary-2' type="3" weight="regular" withMargin={false} modifier='regular'>{isoCodeForGuaranteeCurrency({ currencyId: row.currencyId, currencyLabel: row.currencyLabel }, localCurrencyCode)}</P>
        ),
        grow: 0.8,
      },
      {
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Cap Producto</P>,
        selector: (row: G2Product) => (
          <P variant='secondary-2' type="3" weight="regular" withMargin={false} modifier='regular'>{formatNumber(row.capProduct)}</P>
        ),
        grow: 0.8,
      },
      {
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Utilizado</P>,
        selector: (row: G2Product) => (
          <P variant='secondary-2' type="3" weight="regular" withMargin={false} modifier='regular'>{formatNumber(row.usedProduct)}</P>
        ),
        grow: 0.8,
      },
      {
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Disp. Producto</P>,
        selector: (row: G2Product) => (
          <P variant='secondary-2' type="3" weight="regular" withMargin={false} modifier='regular'>{formatNumber(row.availableProduct)}</P>
        ),
        grow: 0.8,
      },
      {
name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>{fiscalId.availableShortLabel}</P>,
        selector: (row: G2Product) => (
          <P variant='secondary-2' type="3" weight="regular" withMargin={false} modifier='regular'>{formatNumber(row.availableCuitCurrency)}</P>
        ),
        grow: 0.8,
      },
      {
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Disp. operable</P>,
        selector: (row: G2Product) => (
          <P variant='secondary-2' type="3" weight="regular" withMargin={false} modifier='regular'>{formatNumber(row.availableOperable)}</P>
        ),
        grow: 0.8,
      },
      {
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Estado</P>,
        selector: (row: G2Product) => (
          isOperable(row)
            ? <Badge type="text" variant="success" size="sm">Operable</Badge>
            : <Badge type="text" variant="neutral" size="sm">Sin cupo operable</Badge>
        ),
        grow: 1,
      },
      {
        name: '',
        selector: (row: G2Product) => (
          <span onClick={(e) => e.stopPropagation()}>
            <Icon
              name='view_outline'
              variant='info'
              modifier='regular'
              width={24}
              height={24}
              cursor='pointer'
              aria-label={`Ver detalle de ${row.productLabel}`}
              onClick={() => handleProductClick(row)}
            />
          </span>
        ),
        grow: 0.3,
      },
    ];

    return (
      <Table
        columns={columns}
        data={products}
        tableData={{ page: 0, size: products.length, totalElements: products.length, totalPages: 1 }}
        handleChangePage={() => {}}
        onRowClicked={(row: G2Product) => handleProductClick(row)}
      />
    );
  };

  if (loading) {
    return (
      <>
        <div style={{ width: '85vw' }}>{breadcrumb}</div>
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={8} columns={4} />
        </Card>
      </Card>
      </>
    );
  }

  if (error || !header) {
    return (
      <>
        <div style={{ width: '85vw' }}>{breadcrumb}</div>
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card display='flex' justifyContent='center' alignItems='center' height='300px'>
          <P type="1" weight="regular" modifier="weak">{error || 'Aval no encontrado'}</P>
        </Card>
      </Card>
      </>
    );
  }

  return (
    <>
      <div style={{ width: '85vw' }}>{breadcrumb}</div>
    <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      {/* Header with back button and title */}
      <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '4' }}>
        <Button 
          icon='arrow_left_outline'
          variant='tertiary'
          size='sm'
          iconAlign='center'
          onClick={handleGoBack}
          aria-label="Volver a gestión de avales"
        />
        <Card display='flex' alignItems='baseline' gap='8px'>
          <H withMargin={false} variant="secondary-2" type='2' weight='bold' modifier='strong'>
            Gestión de avales
          </H>
          <H variant="secondary-2" type="3" weight="bold" modifier="strong">
            {fiscalId.format(header.cuit)}
          </H>
        </Card>
      </Card>

      {/* Summary Cards - Cap CUIT by Currency (Cap 2) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {renderSummaryCard(
fiscalId.capLabel,
          header.capArs,
          header.capUsd
        )}
        {renderSummaryCard(
          'Utilizado',
          header.usedArs,
          header.usedUsd
        )}
        {renderSummaryCard(
          'Disponible',
          header.availableArs,
          header.availableUsd
        )}
      </div>

      <Card margin={{ bottom: '4' }}>
        <Card margin={{ bottom: '3' }}>
          <H withMargin={false} type='2' weight='bold' modifier='strong'>
            Productos habilitados
          </H>
        </Card>
        
        {renderProductsTable()}
      </Card>
    </Card>
    </>
  );
};

export default GuaranteesDetail;